<template>
  <div class="flex flex-col p-3">
    <div class="flex items-center gap-3 py-2 flex-wrap">
      <input
        type="text"
        v-model="search"
        placeholder="Search tracking / RN"
        class="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary w-52"
        @keyup.enter="onFilterChange"
      />
      <select
        v-model="status"
        class="px-3 py-2 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-primary"
        @change="onFilterChange"
      >
        <option value="">-- Filter by Status --</option>
        <option v-for="item in STATUS_OPTIONS" :key="item" :value="item">{{ formatLabel(item) }}</option>
      </select>
      <select
        v-model="type"
        class="px-3 py-2 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-primary"
        @change="onFilterChange"
      >
        <option value="">-- Filter by Type --</option>
        <option v-for="item in TYPE_OPTIONS" :key="item" :value="item">{{ formatLabel(item) }}</option>
      </select>
      <select
        v-model.number="limit"
        class="px-3 py-2 text-sm border border-gray-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-primary"
        @change="onFilterChange"
      >
        <option :value="12">12</option>
        <option :value="20">20</option>
        <option :value="50">50</option>
        <option :value="100">100</option>
      </select>
      <button
        type="button"
        @click="clearFilters"
        class="px-3 py-2 text-sm font-medium text-primary bg-white border border-primary rounded-md hover:bg-primary hover:text-white transition-colors flex items-center gap-1"
        title="Clear filters"
      >
        <i class="fa fa-times"></i> Clear
      </button>
    </div>

    <div>
      <table>
        <thead>
          <tr>
            <th>#</th>
            <th>Tracking No</th>
            <th>RN</th>
            <th>Type</th>
            <th>Status</th>
            <th>Retries</th>
            <th>Description</th>
            <th>Created</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          <template v-if="loading">
            <tr v-for="i in 6" :key="`sk-${i}`" class="animate-pulse">
              <td v-for="j in 9" :key="j">
                <div class="h-3 bg-gray-200 rounded"></div>
              </td>
            </tr>
          </template>
          <template v-else>
            <tr v-for="(row, idx) in stream" :key="row.id || idx">
              <td>{{ (currentPage - 1) * limit + idx + 1 }}</td>
              <td class="font-mono font-semibold text-primary whitespace-nowrap">{{ row.trackingNo || "—" }}</td>
              <td class="font-mono text-xs whitespace-nowrap">{{ row.rn || "—" }}</td>
              <td class="italic text-blue-600 whitespace-nowrap">{{ formatLabel(row.type) || "—" }}</td>
              <td>
                <span :class="statusPill(row.status)">{{ formatLabel(row.status) || "—" }}</span>
              </td>
              <td class="text-center">{{ row.retries ?? 0 }}</td>
              <td class="max-w-[18rem]">
                <p class="truncate" :title="row.description || undefined">{{ row.description || "—" }}</p>
              </td>
              <td class="text-xs whitespace-nowrap">{{ row.createdAt || "—" }}</td>
              <td>
                <div class="flex gap-1">
                  <button class="btn-sm-primary" type="button" :disabled="!!processingId" @click="view(row)">
                    <i class="fa fa-eye mr-1"></i> View
                  </button>
                  <button
                    type="button"
                    class="bg-green-600 hover:bg-green-700 text-white px-2 py-1 text-xs font-medium rounded disabled:opacity-50 flex items-center transition"
                    :disabled="!row.id || processingId === row.id"
                    @click="runNow(row)"
                  >
                    <i
                      class="fa mr-1"
                      :class="processingId === row.id ? 'fa-spinner fa-spin' : 'fa-play'"
                    ></i>
                    {{ processingId === row.id ? "Running..." : "Run" }}
                  </button>
                </div>
              </td>
            </tr>
          </template>
          <tr v-if="!loading && stream.length === 0">
            <td colspan="9" class="text-center py-6 text-gray-400 text-sm">No results found</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div
      v-if="pagination.totalPages > 1"
      class="flex items-center justify-between px-1 py-3 border-t border-gray-100 mt-2"
    >
      <p class="text-xs text-gray-500">
        Page <span class="font-semibold text-gray-700">{{ currentPage }}</span> of
        <span class="font-semibold text-gray-700">{{ pagination.totalPages }}</span>
        <span class="ml-2 text-gray-400">({{ pagination.totalElements }} records)</span>
      </p>
      <div class="flex gap-2">
        <button class="btn-sm-primary" type="button" @click="previous" :disabled="currentPage === 1">
          <i class="fa fa-chevron-left mr-1"></i> Prev
        </button>
        <button
          class="btn-sm-primary"
          type="button"
          @click="next"
          :disabled="currentPage === pagination.totalPages"
        >
          Next <i class="fa fa-chevron-right ml-1"></i>
        </button>
      </div>
    </div>

    <AppModal v-model="showDetails" size="xl" title="Insolvency Reconciliation Details" hide-footer>
      <div v-if="selected" class="space-y-4 text-sm">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-3 bg-gray-50 p-4 rounded-lg border border-gray-100">
          <p>
            <span class="font-semibold text-gray-500 mr-2">Tracking No:</span>
            <span class="font-mono font-semibold text-primary">{{ selected.trackingNo || "—" }}</span>
          </p>
          <p>
            <span class="font-semibold text-gray-500 mr-2">RN:</span>
            <span class="font-mono">{{ selected.rn || "—" }}</span>
          </p>
          <p>
            <span class="font-semibold text-gray-500 mr-2">Type:</span>
            {{ formatLabel(selected.type) || "—" }}
          </p>
          <p>
            <span class="font-semibold text-gray-500 mr-2">Status:</span>
            <span :class="statusPill(selected.status)">{{ formatLabel(selected.status) || "—" }}</span>
          </p>
          <p>
            <span class="font-semibold text-gray-500 mr-2">Retries:</span>
            {{ selected.retries ?? 0 }}
          </p>
          <p>
            <span class="font-semibold text-gray-500 mr-2">File name:</span>
            <span class="break-all">{{ selected.fileName || "—" }}</span>
          </p>
          <p>
            <span class="font-semibold text-gray-500 mr-2">Created:</span>
            {{ selected.createdAt || "—" }}
          </p>
          <p>
            <span class="font-semibold text-gray-500 mr-2">Updated:</span>
            {{ selected.updatedAt || "—" }}
          </p>
          <p class="md:col-span-2">
            <span class="font-semibold text-gray-500 mr-2">Description:</span>
            {{ selected.description || "—" }}
          </p>
        </div>

        <div v-if="selected.request">
          <h4 class="font-semibold text-gray-700 mb-2">Request</h4>
          <pre
            class="text-green-700 text-xs bg-green-50 border border-green-200 p-3 rounded overflow-auto max-h-48 whitespace-pre-wrap break-all"
          >{{ formatPayload(selected.request) }}</pre>
        </div>

        <div v-if="selected.response">
          <h4 class="font-semibold text-gray-700 mb-2">Response</h4>
          <pre
            class="text-red-700 text-xs bg-red-50 border border-red-200 p-3 rounded overflow-auto max-h-48 whitespace-pre-wrap break-all"
          >{{ formatPayload(selected.response) }}</pre>
        </div>
      </div>
    </AppModal>
  </div>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref } from "vue";
import AppModal from "@/components/ui/AppModal.vue";
import { useToast } from "@/composables";
import { api } from "@/app/services/api.service";

const STATUS_OPTIONS = [
  "PENDING",
  "READY",
  "IN_PROGRESS",
  "EFFECTED",
  "FAILED",
  "CANCELLED",
  "PAUSED",
] as const;

const TYPE_OPTIONS = [
  "DIGITAL_SIGNING",
  "EDMS",
  "DOCUMENT_REGISTRY",
  "PAYMENT",
  "CERTIFICATE",
  "REGISTRY",
] as const;

type ReconciliationRow = {
  id: string;
  createdAt: string | null;
  updatedAt: string | null;
  userId: string | null;
  trackingNo: string | null;
  rn: string | null;
  type: string | null;
  retries: number;
  request: string | null;
  response: string | null;
  status: string | null;
  description: string | null;
  fileName: string | null;
  dependencyId: string | null;
  canceledById: string | null;
};

const { successToast, errorToast } = useToast();

const showDetails = ref(false);
const loading = ref(false);
const currentPage = ref(1);
const limit = ref(12);
const stream = ref<ReconciliationRow[]>([]);
const pagination = reactive({ totalElements: 0, totalPages: 0 });
const status = ref("");
const type = ref("");
const search = ref("");
const selected = ref<ReconciliationRow | null>(null);
const processingId = ref<string | null>(null);

const formatLabel = (value: string | null | undefined) => {
  if (value == null || value === "") return "";
  return value
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const statusPill = (value: string | null | undefined) => {
  const statusValue = String(value || "").toUpperCase();
  const base = "text-xs px-2 py-0.5 rounded-full ";
  if (statusValue === "EFFECTED" || statusValue === "READY") return base + "bg-green-50 text-green-700";
  if (statusValue === "FAILED" || statusValue === "CANCELLED") return base + "bg-red-50 text-red-700";
  if (statusValue === "IN_PROGRESS") return base + "bg-blue-50 text-blue-700";
  if (statusValue === "PAUSED") return base + "bg-orange-50 text-orange-600";
  if (statusValue === "PENDING") return base + "bg-gray-100 text-gray-600";
  return base + "bg-gray-100 text-gray-600";
};

const formatPayload = (value: string | null) => {
  if (!value) return "";
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
};

const fetchData = async () => {
  loading.value = true;
  try {
    const params: Record<string, string | number> = {
      page: currentPage.value,
      size: limit.value,
    };
    if (search.value.trim()) params.search = search.value.trim();
    if (status.value) params.status = status.value;
    if (type.value) params.type = type.value;

    const response = await api().get("/insolvency/reconciliations", { params });
    const data = response.data || {};
    stream.value = Array.isArray(data.content) ? data.content : [];
    pagination.totalElements = Number(data.totalElements) || 0;
    pagination.totalPages = Number(data.totalPages) || 0;
  } catch (error: unknown) {
    stream.value = [];
    pagination.totalElements = 0;
    pagination.totalPages = 0;
    const err = error as { response?: { data?: { message?: string } }; message?: string };
    errorToast(
      "Error",
      err.response?.data?.message || err.message || "Failed to fetch insolvency reconciliations"
    );
  } finally {
    loading.value = false;
  }
};

const onFilterChange = () => {
  currentPage.value = 1;
  fetchData();
};

const clearFilters = () => {
  search.value = "";
  status.value = "";
  type.value = "";
  currentPage.value = 1;
  fetchData();
};

const view = (row: ReconciliationRow) => {
  selected.value = row;
  showDetails.value = true;
};

const runNow = async (row: ReconciliationRow) => {
  if (!row.id) return;
  processingId.value = row.id;
  try {
    await api().post(`/insolvency/reconciliations/${encodeURIComponent(row.id)}`);
    successToast("Success", "Reconciliation run started");
    await fetchData();
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } }; message?: string };
    errorToast("Error", err.response?.data?.message || err.message || "Failed to run reconciliation");
  } finally {
    processingId.value = null;
  }
};

const next = () => {
  if (currentPage.value < pagination.totalPages) {
    currentPage.value++;
    fetchData();
  }
};

const previous = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
    fetchData();
  }
};

onMounted(() => fetchData());
</script>

<style scoped>
table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}
th {
  text-align: left;
  padding: 0.75rem 1rem;
  font-weight: 600;
  color: #374151;
  border-bottom: 1px solid #e5e7eb;
}
td {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e5e7eb;
  font-size: 0.875rem;
}
</style>
